"use client";

import { motion } from "framer-motion";
import { useEffect, useRef, useState } from "react";

export function PixelTrailGrid({ className }: { className?: string }) {
    const [blocks, setBlocks] = useState<{ x: number, y: number, key: string }[]>([]);
    const containerRef = useRef<HTMLDivElement>(null);

    // We'll use a simple grid size
    const CELL_SIZE = 40;

    useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
            if (!containerRef.current) return;

            const rect = containerRef.current.getBoundingClientRect();
            const x = e.clientX - rect.left;
            const y = e.clientY - rect.top;

            // Only trigger if mouse is inside the container
            if (x >= 0 && y >= 0 && x <= rect.width && y <= rect.height) {
                const gridX = Math.floor(x / CELL_SIZE) * CELL_SIZE;
                const gridY = Math.floor(y / CELL_SIZE) * CELL_SIZE;

                setBlocks((prev) => {
                    const last = prev[prev.length - 1];
                    if (last && last.x === gridX && last.y === gridY) return prev;

                    const newBlock = { x: gridX, y: gridY, key: crypto.randomUUID() };
                    // Keep only last 20 blocks for performance
                    return [...prev.slice(-20), newBlock];
                });
            }
        };

        window.addEventListener("mousemove", handleMouseMove);
        return () => window.removeEventListener("mousemove", handleMouseMove);
    }, []);

    return (
        <div
            ref={containerRef}
            className={`absolute inset-0 bg-white overflow-hidden ${className}`}
        >
            {/* Static Base Grid */}
            <div
                className="absolute inset-0 z-0 opacity-[0.2]"
                style={{
                    backgroundImage: `linear-gradient(#e5e5e5 1px, transparent 1px), linear-gradient(90deg, #e5e5e5 1px, transparent 1px)`,
                    backgroundSize: `${CELL_SIZE}px ${CELL_SIZE}px`
                }}
            />

            {/* Active Blocks */}
            {blocks.map((block) => (
                <FadeBlock key={block.key} x={block.x} y={block.y} size={CELL_SIZE} />
            ))}

            <div className="absolute inset-0 bg-gradient-to-t from-white via-transparent to-transparent pointer-events-none z-10" />
        </div>
    );
}

function FadeBlock({ x, y, size }: { x: number, y: number, size: number }) {
    return (
        <motion.div
            initial={{ opacity: 1, scale: 1 }}
            animate={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="absolute bg-gray-100 border border-gray-200 pointer-events-none"
            style={{
                left: x,
                top: y,
                width: size,
                height: size,
            }}
        />
    );
}
